import React from 'react';
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';
import {
  BodyTextMedium,
  BodyTextLarge,
  Grid,
  GridItem,
  HeadingText30,
  HeadingText60,
  HeadingText20,
} from '@modernhealth/vital-components';
import { breakpointValues, colors } from '@modernhealth/vital-foundation';

import { mediaQueryMinWidth } from 'src/shared/utils';
import { Head } from 'src/shared/components/Head';
import { useTranslation } from 'src/shared/hooks/formatMessage';
import CirclesImage from 'src/assets/images/CircleSession.png';
import { ReactComponent as CommentImage } from 'src/assets/images/Comment.svg';

import messages from './AboutCircles.messages';

const Wrapper = styled.div({
  paddingBottom: 100,
});

const HeadingWrapper = styled.div({
  width: '100%',
  marginBottom: 48,
  [mediaQueryMinWidth(breakpointValues.md)]: {
    width: '60%',
  },
});

const CommentText = styled(BodyTextMedium)`
  line-height: 32px;
`;

const ContactWrapper = styled.div({
  padding: 32,
  borderRadius: 5,
  backgroundColor: colors.gray05,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const ContactLink = styled.a({
  color: colors.blue55,
  textDecoration: 'underline',
  cursor: 'pointer',
});

const JoinText = styled(BodyTextMedium)`
  text-align: center;
`;

const ImageWrapper = styled.div({
  paddingRight: 0,
  [mediaQueryMinWidth(breakpointValues.xl)]: {
    paddingRight: 130,
  },
});

const AboutCircles: React.FC<unknown> = () => {
  const { $t } = useTranslation();

  const header = {
    title: `${$t(messages.header.title)} | Modern Health`,
    description: $t(messages.header.description),
  };

  return (
    <Wrapper>
      <Head>
        <title>{header.title}</title>
        <meta property="og:title" content={header.title} />
        <meta property="og:description" content={header.description} />
        <meta
          property="og:image"
          content="https://assets.joinmodernhealth.com/circles_illustrations/circles-about-og-image.png "
        />
      </Head>
      <HeadingWrapper>
        <HeadingText60>{$t(messages.header.title)}</HeadingText60>
        <BodyTextLarge fontColor="gray65" marginTop={8}>
          <FormattedMessage defaultMessage="Circles are designed to be spaces for us to collectively learn, grow, and connect across topics that impact our day-to-day lives" />
        </BodyTextLarge>
      </HeadingWrapper>
      <Grid>
        <GridItem lg={3} md={2}>
          <ImageWrapper>
            <img alt="" src={CirclesImage} />
          </ImageWrapper>
        </GridItem>
        <GridItem lg={2} md={3} column>
          {messages.cards.map((card, i) => {
            return (
              <Card
                key={i}
                image={card.image}
                title={$t(card.title)}
                description={$t(card.description)}
              />
            );
          })}
        </GridItem>
      </Grid>
      <HeadingText60 marginTop={48} marginBottom={48}>
        <FormattedMessage defaultMessage="What people are saying" />
      </HeadingText60>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Quote>
          <FormattedMessage
            description="The speaker is a woman"
            defaultMessage="This is such a great resource and I am so grateful that this exists. I've
                    been to a few Circles now—Healing Circles for the Black Community, Celebrating
                    Black Greatness, and Race & Mental Health—and each time I come away feeling
                    refreshed and renewed by being in a safe space where I am understood and seen."
          />
          <FormattedMessage
            description="The participant is a woman"
            defaultMessage="Circles Participant"
          />
        </Quote>

        <Quote>
          <FormattedMessage
            description="The speaker is a man"
            defaultMessage="
                Thank you for having a healing Circle for the Asian community to
                be able to come together and process. It is a safe space for
                individuals to participate without judgement and discuss mental
                health; especially when it is such a taboo subject within the
                Asian community. I felt like I was seen for the first time when
                I attended today's healing Circle, knowing that I am not alone
                in my emotions that have stemmed from past/present experiences."
          />
          <FormattedMessage
            description="The participant is a man"
            defaultMessage="Circles Participant"
          />
        </Quote>
      </div>
      <ContactWrapper>
        <HeadingText30 marginBottom={16}>
          Modern Health is currently accessible as a benefit through your employer.
        </HeadingText30>
        <BodyTextMedium fontColor="gray65">
          Not yet a member, and want to bring Modern Health to your organization?{' '}
          <ContactLink href="https://www.modernhealth.com/request-for-demo" target="_blank">
            Reach out to us!
          </ContactLink>{' '}
          Or learn more about{' '}
          <ContactLink href="https://www.modernhealth.com/" target="_blank">
            Modern Health
          </ContactLink>
        </BodyTextMedium>
      </ContactWrapper>
      <JoinText marginTop={32} fontColor="gray65">
        Already Modern Health member?{' '}
        <ContactLink href="https://my.joinmodernhealth.com/" target="_blank">
          Sign in
        </ContactLink>
      </JoinText>
    </Wrapper>
  );
};

const CardWrapper = styled.div({
  marginBottom: 48,
  display: 'flex',
  flexDirection: 'row',
  [mediaQueryMinWidth(breakpointValues.xl)]: {
    paddingLeft: 30,
  },
});

const Card = ({
  title,
  description,
  image,
}: {
  title: string;
  description: string;
  image: string;
}) => {
  return (
    <CardWrapper>
      <img
        style={{
          height: 25,
          width: 25,
          marginRight: 16,
          marginTop: 5,
        }}
        src={image}
      />
      <div>
        <HeadingText30>{title}</HeadingText30>
        <BodyTextMedium fontColor="gray65" marginTop={8}>
          {description}
        </BodyTextMedium>
      </div>
    </CardWrapper>
  );
};

const QuoteWrapper = styled.div({
  width: '100%',
  marginBottom: 48,
  [mediaQueryMinWidth(breakpointValues.md)]: {
    width: '45%',
  },
});

const Quote: React.FC<{ children: [comment: React.ReactNode, attribution: React.ReactNode] }> = ({
  children,
}) => {
  const [comment, attribution] = children;

  return (
    <QuoteWrapper>
      <CommentImage />
      <CommentText fontColor="gray65" marginTop={12}>
        {comment}
      </CommentText>
      <HeadingText20 marginTop={16}>{attribution}</HeadingText20>
    </QuoteWrapper>
  );
};

export default AboutCircles;
